import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/api';
import { FaGavel, FaFileAlt } from 'react-icons/fa';

function BrokerActivitiesPage() {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('bids');
    const [bids, setBids] = useState([]);
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchData = useCallback(async () => {
        if (!user?.profileId) return;
        setLoading(true);
        setError('');
        try {
            const [bidsRes, appsRes] = await Promise.all([
                apiClient.get(`/bids/worker/${user.profileId}`),
                apiClient.get(`/jobs/worker/${user.profileId}/applications`)
            ]);
            setBids(bidsRes.data || []);
            setApplications(appsRes.data || []);
        } catch (err) {
            console.error("Failed to fetch activities:", err);
            setError(err.response?.data?.message || err.message || 'Could not load your activities.');
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-bold text-gray-800 flex items-center">
                <FaGavel className="mr-3 text-blue-500" /> My Activities
            </h1>

            {error && <div className="bg-red-100 text-red-700 p-4 rounded-lg">{error}</div>}

            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="flex border-b">
                    <button
                        className={`flex-1 py-4 text-center font-semibold transition-colors ${activeTab === 'bids' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                        onClick={() => setActiveTab('bids')}
                    >
                        My Bids ({bids.length})
                    </button>
                    <button
                        className={`flex-1 py-4 text-center font-semibold transition-colors ${activeTab === 'applications' ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600' : 'text-gray-600 hover:bg-gray-50'}`}
                        onClick={() => setActiveTab('applications')}
                    >
                        My Applications ({applications.length})
                    </button>
                </div>

                <div className="p-6">
                    {loading ? <p className="text-center py-8 text-gray-500">Loading activities...</p> : (
                        activeTab === 'bids' ? (
                            bids.length > 0 ? (
                                <div className="space-y-4">
                                    {bids.map(bid => (
                                        <div key={bid.id} className="p-4 border rounded-xl hover:shadow-md transition-shadow">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <h4 className="font-bold text-lg text-gray-800">{bid.job?.title || 'Job Title Unavailable'}</h4>
                                                    <p className="text-sm text-gray-500">Bid Amount: <span className="font-mono font-semibold text-gray-700">৳{bid.bidAmount}</span></p>
                                                </div>
                                                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${bid.status === 'accepted' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                    {bid.status || 'Pending'}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : <p className="text-center py-8 text-gray-500">No bids placed yet.</p>
                        ) : (
                            applications.length > 0 ? (
                                <div className="space-y-4">
                                    {applications.map(app => (
                                        <div key={app.id} className="p-4 border rounded-xl hover:shadow-md transition-shadow">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <h4 className="font-bold text-lg text-gray-800">{app.job_details?.job_post?.title || 'Job Title Unavailable'}</h4>
                                                    <p className="text-sm text-gray-500 mt-1">{app.cover_letter ? `"${app.cover_letter.substring(0, 50)}..."` : 'No cover letter'}</p>
                                                </div>
                                                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${app.status === 'accepted' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                    {app.status || 'Pending'}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : <p className="text-center py-8 text-gray-500">No job applications submitted yet.</p>
                        )
                    )}
                </div>
            </div>
        </div>
    );
}

export default BrokerActivitiesPage;
